import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import { getSingleOrder } from '../apiClient';

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getSingleOrder(orderId);
        setOrder(data);
      } catch (err) {
        console.error(`Error fetching order ${orderId} details:`, err);
        setError('Failed to load order details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrderDetails();
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p>Loading order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-gray-600">Order not found.</p>
      </div>
    );
  }

  return (
    <SessionAuth>
      <div className="container mx-auto p-6 bg-background rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Order Details (ID: {order.id})</h1>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Order Summary</h2>
          <p className="mb-3"><strong>Total Amount:</strong> ${order.total_amount.toFixed(2)} {order.currency.toUpperCase()}</p>
          <p className="mb-3"><strong>Quantity:</strong> {order.quantity}</p>
          <p className="mb-3"><strong>Payment Status:</strong> {order.payment_status}</p>
          <p className="mb-3"><strong>Fulfillment Status:</strong> {order.fulfillment_status}</p>
          <p className="mb-3"><strong>Ordered On:</strong> {new Date(order.created_at).toLocaleString()}</p>
          {order.updated_at && <p className="mb-3"><strong>Last Updated:</strong> {new Date(order.updated_at).toLocaleString()}</p>}
          {order.payment_intent_id && <p className="mb-3"><strong>Payment Intent ID:</strong> {order.payment_intent_id}</p>}
          {order.printful_order_id && <p className="mb-3"><strong>Printful Order ID:</strong> {order.printful_order_id}</p>}
        </div>

        {order.mockup && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Mockup Details</h2>
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
              <div className="w-full md:w-1/2 p-4 bg-gray-50 rounded-lg">
                <img src={order.mockup.mockup_image_url} alt="Mockup" className="w-full h-auto rounded-md shadow-sm" />
              </div>
              <div className="w-full md:w-1/2 p-4 bg-gray-50 rounded-lg">
                <p className="mb-3"><strong>Original Image:</strong> <a href={order.mockup.original_image_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">View Image</a></p>
                <p className="mb-3"><strong>Prompt:</strong> {order.mockup.prompt}</p>
                <p className="mb-3"><strong>Style:</strong> {order.mockup.style}</p>
                <p className="mb-3"><strong>T-shirt Model:</strong> {order.mockup.tshirt_model_name}</p>
                <p className="mb-3"><strong>T-shirt Variant ID:</strong> {order.mockup.tshirt_variant_id}</p>
                <p className="mb-3"><strong>Image Placement:</strong> {order.mockup.image_placement}</p>
                {order.mockup.stitch_color && <p className="mb-3"><strong>Stitch Color:</strong> {order.mockup.stitch_color}</p>}
              </div>
            </div>
          </div>
        )}

        {order.shipping_address && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Shipping Address</h2>
            <p className="text-gray-700 mb-1">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
            <p className="text-gray-700 mb-1">{order.shipping_address.address1}</p>
            <p className="text-gray-700 mb-1">{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
            <p className="text-gray-700 mb-1">{order.shipping_address.country_code}</p>
            {order.shipping_address.email && <p className="mb-1">Email: {order.shipping_address.email}</p>}
            {order.shipping_address.phone && <p className="mb-1">Phone: {order.shipping_address.phone}</p>}
          </div>
        )}

        <div className="text-center">
          <Link to="/orders" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md">
            Back to Order History
          </Link>
        </div>
      </div>
    </SessionAuth>
  );
};

export default OrderDetailsPage;
